import moment from "moment-timezone";
import { useState } from "react";
import LinkButton from "src/components/buttons/LinkButton";
import ClassDetail from "src/components/items/ClassDetail";
import { AddStudentToRegistrationModal } from "src/components/modals/AddStudentToRegistrationModal";
import ClassModel from "src/models/ClassModel";
import GroupRegistrationModel from "src/models/GroupRegistrationModel";
import { useSession } from "src/stores/Session";
import { useStore } from "src/stores/Store";
import { dateFormatLong } from "src/utils/constants";
import { modals } from "src/utils/modals";
import tw from "src/utils/tw";
import {
  CapacityStatus,
  GroupRegistrationStatus,
  IntegrationType,
  MembershipStatus,
  OfferingStatus,
} from "types/code-generator";
import { BorderButton } from "../buttons/BorderButton";
import { Tag } from "../forms/Tag";
import { OfferingDetails } from "./OfferingDetails";
import ShowEnrollments from "./ShowEnrollments";
import { ItemContent } from "./item";
import ItemCard from "./item/ItemCard";

interface Props {
  classItem: ClassModel;
  isPreview?: boolean;
  isDetail?: boolean;
  onExpand?: () => void;
  hideAddButton?: boolean;
  hideDetails?: boolean;
  loading?: boolean;
  onOutsideClick?: () => void;
}

interface ClassHasRoomBarProps {
  hasRoom: boolean;
  className?: string;
}

const ClassHasRoomBar = (props: ClassHasRoomBarProps) => {
  return (
    <span
      className={tw(
        "absolute",
        "left-0",
        "top-4",
        "bottom-4",
        "w-1",
        props.hasRoom ? "bg-success" : "bg-error"
      )}
    />
  );
};

export default function Class(props: Props) {
  const [showingRoster, setShowingRoster] = useState(false);

  const store = useStore();
  const session = useSession();

  const classItem = new ClassModel(props.classItem);

  function showRoster() {
    setShowingRoster((current) => !current);

    if (props.onExpand) {
      setTimeout(() => {
        props.onExpand();
      }, 250);
    }
  }

  function renderClassStatus(registrationStatus) {
    const waitlist = classItem.getWaitlist();
    const roster = classItem.getRoster();
    let content;

    if (classItem.displayOpenSpots || (store.isAdminArea && !props.isPreview)) {
      if (classItem.maxEnrollment === null) {
        content = <>Unlimited spots</>;
      } else {
        let spotsRemaining = classItem.maxEnrollment - roster.length;

        if (
          session.groupRegistrations.find((gr) => gr.class.id === classItem.id)
        ) {
          spotsRemaining -= 1;
        }

        spotsRemaining = Math.max(0, spotsRemaining);

        if (
          classItem.capacity === CapacityStatus.Full ||
          (classItem.capacity === CapacityStatus.WaitlistAvailable &&
            waitlist.length > 0)
        ) {
          spotsRemaining = 0;
        }

        content = (
          <>
            {spotsRemaining} spot{spotsRemaining !== 1 && "s"} open
          </>
        );
      }
    }

    if (props.hideAddButton) {
      return null;
    }

    return content;
  }

  function removeRegistration(id) {
    const registration = session.groupRegistrations.find(
      (r) => r.class.id === classItem.id && r.student && r.student.id === id
    );

    session.removeRegistration(registration.id);
  }

  function showStudentsTags() {
    const studentsInClass = session.getStudentsInClass(classItem.id);

    if (studentsInClass.length === 0) {
      return null;
    }

    // if (classItem.semiPrivate) {
    //   const registrations = session.groupRegistrations.filter(
    //     (r) => r.class.id === classItem.id
    //   );

    //   return (
    //     <div className="mt-4 flex gap-4">
    //       {registrations.map((r) => {
    //         return (
    //           <Tag
    //             closable={true}
    //             onClose={() => removeRegistration(r.id)}
    //             key={r.id}
    //           >
    //             {r.roster.map((s) => s.firstName).join(", ")}{" "}
    //             <EditButton onClick={() => selectSemiPrivateRegistration(r)}>
    //               Edit
    //             </EditButton>
    //           </Tag>
    //         );
    //       })}
    //     </div>
    //   );
    // }

    return (
      <div className="mt-4 flex flex-wrap gap-4">
        {studentsInClass.map((student) => {
          return (
            <Tag
              key={student.id}
              closable={true}
              onClose={() => removeRegistration(student.id)}
            >
              {student.firstName}
            </Tag>
          );
        })}
      </div>
    );
  }

  // function selectSemiPrivateRegistration(semiPrivateRegistration) {
  //   modals.show(AddStudentToRegistrationModal, {
  //     semiPrivateRegistration,
  //   });
  // }

  function selectClass() {
    modals.show(AddStudentToRegistrationModal, {
      classItem,
    });
  }

  let classDetail;

  const currentClass = new ClassModel({
    ...classItem,
    registrations: [...classItem.registrations],
  });

  if (session.groupRegistrations.find((gr) => gr.class.id === classItem.id)) {
    currentClass.registrations.push(
      new GroupRegistrationModel({
        status: GroupRegistrationStatus.Approved,
      })
    );
  }

  if (props.isDetail) {
    classDetail = (
      <ClassDetail
        classItem={classItem}
        isDetail={props.isDetail}
        onExpand={props.onExpand}
      />
    );
  }

  if (props.hideDetails) {
    classDetail = null;
  }

  let addBtnText = `Add ${currentClass.participantType}`;

  // console.log("registrationStatus", registrationStatus);

  const registrationStatus = currentClass.capacity;
  const waitlist = classItem.getWaitlist();
  const isFull =
    registrationStatus === CapacityStatus.Full ||
    (registrationStatus === CapacityStatus.WaitlistAvailable &&
      waitlist.length > 0 &&
      waitlist.length >= classItem.waitlistCapacity);
  let membersOnly = !currentClass.allowNonMembers;
  let userMemberships = store.user?.memberships || [];
  let students = store.user?.students || [];

  if (store.user?.primaryAccount) {
    userMemberships = store.user.primaryAccount.memberships || [];
    students = store.user.primaryAccount.students || [];
  }

  let isMember = Boolean(
    userMemberships.find(
      (m) =>
        currentClass.membershipOfferings.find((mo) => mo.id === m.offeringId) &&
        m.status === MembershipStatus.Active
    ) ||
      session.memberships.find(
        (sm) =>
          sm.offering.autoEnroll &&
          currentClass.membershipOfferings.find(
            (mo) => mo.id === sm.offering.id
          )
      )
  );

  if (props.classItem.requiresUSASwimming) {
    const hasRequiredMembership = students.some((m) => m.usaSwimmingMemberId);
    membersOnly = true;
    isMember = isMember && hasRequiredMembership;
  }

  if (
    props.classItem.integration === IntegrationType.USASwimming &&
    !props.classItem.requiresUSASwimming &&
    props.classItem.membershipOfferings.length > 1
  ) {
    // USA Swimming not required, but additional are

    const hasRequiredMemberships = props.classItem.membershipOfferings.some(
      (mo) =>
        userMemberships.find(
          (m) => m.offeringId === mo.id && m.status === MembershipStatus.Active
        ) ||
        session.memberships.find(
          (sm) => sm.offering.autoEnroll && sm.offering.id === mo.id
        )
    );

    isMember = hasRequiredMemberships && isMember;
  }

  if (
    registrationStatus === CapacityStatus.Waitlist ||
    (registrationStatus === CapacityStatus.WaitlistAvailable &&
      waitlist.length > 0 &&
      waitlist.length < classItem.waitlistCapacity)
  ) {
    addBtnText = `Add ${currentClass.participantType} to Waitlist`;
  } else if (isFull) {
    addBtnText = `${currentClass.type} Full`;
  }

  // console.log("isMember", isMember);

  let registrationOpen =
    classItem.registrationOpen && moment(classItem.registrationOpen);
  const registrationClose =
    classItem.registrationClose && moment(classItem.registrationClose);
  const today = moment();

  if (classItem.registrationAdjustments.length > 0 && store.user) {
    const adjustments = classItem.registrationAdjustments.filter((ra) => {
      const hasMembership = userMemberships.find(
        (m) =>
          m.offeringId === ra.membershipOfferingId &&
          m.status === MembershipStatus.Active
      );

      const hasMembershipInCart =
        classItem.autoEnroll &&
        session.memberships.find(
          (m) => m.offeringId === ra.membershipOfferingId
        );

      return (
        hasMembership ||
        hasMembershipInCart ||
        (classItem.registrationAdjustments.length === 1 &&
          ra.membershipOfferingId === null)
      );
    });

    const adjustment =
      adjustments.length > 0 &&
      moment.min(...adjustments.map((ra) => moment(ra.registrationOpen)));

    if (adjustment) {
      registrationOpen = adjustment.clone();
    }
  }

  let detailDisabled = false;

  if (registrationOpen && today.isBefore(registrationOpen)) {
    addBtnText = `Opens ${registrationOpen.format(dateFormatLong)}`;
    detailDisabled = true;
  }

  if (
    (registrationClose && today.isAfter(registrationClose)) ||
    !registrationOpen
  ) {
    addBtnText = `Registration Closed`;
    detailDisabled = true;
  }

  // console.log("membersOnly", membersOnly);
  // console.log("isMember", isMember);

  let addButton = (
    <BorderButton
      disabled={
        isFull ||
        detailDisabled ||
        (membersOnly && !isMember) ||
        props.classItem.status === OfferingStatus.Archived
      }
      type="submit"
      onClick={selectClass}
    >
      {addBtnText}
    </BorderButton>
  );

  if (store.isAdminArea && !props.isPreview) {
    addButton = (
      <BorderButton onClick={selectClass} type="submit">
        {addBtnText}
      </BorderButton>
    );
  }

  if (props.hideAddButton) {
    addButton = null;
  }

  if (props.classItem.requiresRegistration === false) {
    addButton = null;
  }

  // console.log("roster", roster);

  return (
    <ItemCard
      loading={props.loading === undefined ? true : props.loading}
      className={tw(props.isDetail && "mb-0 border-b-0")}
      // isDetail={isDetail || isPreview}
      // type={!isDetail && !isPreview && "communication"}
    >
      {store.isAdminArea && !props.isPreview && !props.isDetail && (
        <ClassHasRoomBar
          hasRoom={
            registrationStatus !== CapacityStatus.Full &&
            registrationStatus !== CapacityStatus.Waitlist
          }
        />
      )}
      <ItemContent>
        {/* {classItem.private && <ItemLabel>{classItem.type}</ItemLabel>} */}
        <div className="mb-4">
          <OfferingDetails
            offering={classItem}
            showPrice
            linkToOffering
            isPreview={props.isPreview}
            onOutsideClick={props.onOutsideClick}
          />
        </div>
      </ItemContent>
      {store.isAdminArea && !props.isPreview && (
        <div className="mb-4 text-sm">
          <ShowEnrollments
            onClick={showRoster}
            class={classItem}
            visible={showingRoster}
          />
        </div>
      )}
      <div className="flex items-center gap-4">
        <div className="xw-full">{addButton}</div>
        {(!store.isAdminArea || props.isPreview) && (
          <div className="">{renderClassStatus(registrationStatus)}</div>
        )}
      </div>

      {membersOnly && props.classItem.requiresRegistration !== false && (
        <div className="pt-2 text-sm text-empty">
          This {classItem.type.toLowerCase()} is available to select members.{" "}
          {props.isDetail ? (
            <>See details below.</>
          ) : (
            <LinkButton to={`/find/${classItem.id}`}>
              <strong>View details</strong>
            </LinkButton>
          )}
        </div>
      )}

      {!props.isPreview && showStudentsTags()}

      {classDetail}
    </ItemCard>
  );
}
